// src/pages/PaymentStatus.js
import React, { useEffect, useState } from 'react';
import NotificationPrompt from '../components/NotificationPrompt';
import usePWAInstall from '../hooks/usePWAInstall';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useAuthState } from '../hooks/useAuthState';
import { markSuccessfulRegistration } from '../services/userRegistrationService';

/**
 * PaymentStatus Component
 * 
 * This component displays the payment status after a user completes event registration.
 * It implements the enhanced notification system that only shows notification permission
 * requests when both conditions are met:
 * 1. PWA is successfully installed
 * 2. User has successfully completed event registration
 * 
 * Key Features:
 * - Comprehensive payment status display
 * - Strategic notification permission timing
 * - User registration tracking integration
 * - Professional UI with clear feedback
 * - Error handling and validation
 * 
 * @returns {JSX.Element} The payment status page
 */
const PaymentStatus = () => {
  // PWA and user state management
  const { isInstalled } = usePWAInstall();
  const [user, loading] = useAuthState(auth);
  
  // Component state
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [registrationTracked, setRegistrationTracked] = useState(false);

  /**
   * Fetch payment details and handle post-payment logic
   * Includes registration tracking and notification eligibility
   */
  useEffect(() => {
    const fetchPaymentDetails = async () => {
      try {
        // Extract payment parameters from URL
        const status = searchParams.get('status');
        const eventId = searchParams.get('eventId');
        const registrationId = searchParams.get('registrationId');

        // Validate required parameters
        if (!eventId || !registrationId) {
          throw new Error('Missing registration details');
        }

        // Fetch registration details from Firestore
        const registrationRef = doc(db, `registrations/${eventId}/registrations/${registrationId}`);
        const registrationSnap = await getDoc(registrationRef);

        if (!registrationSnap.exists()) {
          throw new Error('Registration not found');
        }

        const registrationData = registrationSnap.data();

        // Prepare payment details for display
        const paymentInfo = {
          status,
          registrationId,
          eventId,
          timestamp: searchParams.get('timestamp'),
          transactionId: searchParams.get('txnid'),
          error: searchParams.get('error'),
          amount: registrationData.paymentDetails?.totalAmount,
          eventName: registrationData.eventTitle,
          registrationData
        };

        setPaymentDetails(paymentInfo);

        // Handle successful payment logic
        if (status === 'success' && user && !registrationTracked) {
          console.log('Processing successful payment...');
          
          // Mark this as a successful registration for notification eligibility
          const tracked = await markSuccessfulRegistration(user.uid, eventId, registrationId);
          setRegistrationTracked(tracked);
          
          // Check if user is eligible for notification permission request
          // This will be handled by the NotificationPrompt component based on:
          // 1. PWA installation status
          // 2. Successful registration completion (just tracked above)
          if (isInstalled && tracked) {
            console.log('User meets criteria for notification permission request');
            // Small delay to ensure a smooth user experience
            setTimeout(() => {
              setShowNotificationPrompt(true);
            }, 1500);
          } else {
            console.log('User not eligible for notification prompt:', {
              isInstalled,
              tracked,
              userId: user?.uid
            });
          }
        }
      } catch (err) {
        console.error('Error fetching payment details:', err);
        setError(err.message);
      } finally {
        setPageLoading(false);
      }
    };

    // Only fetch details when user authentication is resolved
    if (!loading) {
      fetchPaymentDetails();
    }
  }, [searchParams, user, loading, isInstalled, registrationTracked]);

  /**
   * Handle notification prompt closure
   * Clean up state after user interaction
   */
  const handleNotificationPromptClose = () => {
    setShowNotificationPrompt(false);
    console.log('Notification prompt closed by user');
  };

  // Loading state
  if (pageLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading payment details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
          <div className="text-center">
            <div className="text-red-500 text-5xl mb-4">❌</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Error</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition"
            >
              Return Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
        <div className="text-center">
          {paymentDetails.status === 'success' ? (
            <>
              {/* Success State */}
              <div className="text-green-500 text-5xl mb-4">✅</div>
              <div className="space-y-2 mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Registration Successful!</h2>
                <p className="text-green-600 font-medium">Payment Completed Successfully</p>
              </div>
              
              {/* Registration Verification Notice */}
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-6">
                <h3 className="text-indigo-800 font-medium mb-2">🔍 Registration Verification</h3>
                <p className="text-indigo-700 text-sm leading-relaxed">
                  Our staff will verify your registration against our rules and regulations.  <br/><br/>
                  Our staff will contact you through your provided contact details with the status of your registration.
                </p>
              </div>
              
              {/* PWA Installation Reminder (if applicable) */}
              {!isInstalled && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <h3 className="text-blue-800 font-medium mb-2">📱 Install Our App</h3>
                  <p className="text-blue-700 text-sm leading-relaxed">
                    Install our app to receive event notifications and updates!
                  </p>
                </div>
              )}
         
            </>
          ) : (
            <>
              {/* Failure State */}
              <div className="text-red-500 text-5xl mb-4">⚠️</div>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Payment Failed</h2>
              <p className="text-red-600 mb-6">
                We couldn't process your payment. Please try again or contact support.
              </p>
            </>
          )}

          {/* Payment Details */}
          <div className="space-y-4 text-left mb-6">
            <div className="border-b pb-2">
              <p className="text-gray-600 text-sm">Event</p>
              <p className="font-semibold text-gray-800">{paymentDetails.eventName}</p>
            </div>
            <div className="border-b pb-2">
              <p className="text-gray-600 text-sm">Registration ID</p>
              <p className="font-semibold text-gray-800 font-mono text-sm">{paymentDetails.registrationId}</p>
            </div>
            {paymentDetails.transactionId && (
              <div className="border-b pb-2">
                <p className="text-gray-600 text-sm">Transaction ID</p>
                <p className="font-semibold text-gray-800 font-mono text-sm">{paymentDetails.transactionId}</p>
              </div>
            )}
            {paymentDetails.amount && (
              <div className="border-b pb-2">
                <p className="text-gray-600 text-sm">Amount</p>
                <p className="font-semibold text-gray-800">₹{paymentDetails.amount}</p>
              </div>
            )}
            {paymentDetails.error && (
              <div className="border-b pb-2">
                <p className="text-gray-600 text-sm">Error Details</p>
                <p className="text-red-500 text-sm">{paymentDetails.error}</p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3">
            <button
              onClick={() => navigate('/')}
              className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Return Home
            </button>
            
            {paymentDetails.status === 'success' && (
              <button
                onClick={() => navigate('/account')}
                className="bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 transition-colors"
              >
                View My Account
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Enhanced Notification Prompt - Only shows when eligible */}
      <NotificationPrompt
        show={showNotificationPrompt}
        onClose={handleNotificationPromptClose}
        userId={user?.uid}
      />
    </div>
  );
};

export default PaymentStatus;